import { saveCriticalState } from './stateRescue'

/**
 * Safely imports a dynamic module, handling chunk load errors by performing a full page reload.
 * This is crucial for SPAs when a new version is deployed and old chunks are removed from the server.
 * 
 * @param importFn The dynamic import function, e.g., () => import('./MyComponent.vue')
 * @returns A promise that resolves to the module or reloads the page on failure
 */
function isEmptyModule(module: unknown): boolean {
  if (module == null) {
    return true
  }
  if (typeof module === 'object' && 'default' in module) {
    return (module as { default?: unknown }).default == null
  }
  return false
}

export function safeLazyLoad<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().then((module) => {
    if (isEmptyModule(module)) {
      const error = new Error('Dynamic import returned an empty module')
      error.name = 'ChunkLoadError'
      throw error
    }
    return module
  }).catch((error) => {
    // Check if a vite preload error was already detected and reload is in progress
    const reloadInProgress = (window as any).__VITE_RELOAD_IN_PROGRESS__;
    if (reloadInProgress) {
      console.log('⚡ Reload already triggered, returning never-resolving promise to pause router');
      return new Promise<T>(() => {});
    }

    // Extended error detection for Safari, Firefox, Chrome, and other browsers
    // Chunk errors can manifest as ChunkLoadError, TypeError, SyntaxError, or network errors
    const isChunkLoadError = 
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('Failed to import') ||
      error.message?.includes('chunk') ||
      error.message?.includes('Failed to fetch') ||
      (error.name === 'TypeError' && error.message?.includes('dynamic import')) ||
      (error.name === 'SyntaxError' && error.message?.includes('Unexpected token')) ||
      // Safari-specific: Script loading failures show up as different error formats
      error.message?.includes('Script error') ||
      error.message?.includes('Load failed') ||
      error.message?.includes('error loading dynamically imported module') ||
      error.message?.includes('Cancelled') ||
      error.stack?.includes('chunk');

    if (isChunkLoadError) {
      console.error('🔴 Chunk load error detected. Reloading page to fetch latest version...', error);
      
      // Try to rescue state before reloading (e.g. form data in newEventStore)
      saveCriticalState();
      
      // Store in session storage that we have just reloaded due to a chunk error
      // to avoid infinite reload loops if the server is actually down
      const RELOAD_KEY = 'app_reload_attempted';
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();
      
      // If we reloaded less than 10 seconds ago, pause router instead of throwing
      // (avoids Vue Router "Couldn't resolve component" surfacing to Sentry)
      if (lastReload && (now - parseInt(lastReload)) < 10000) {
        console.error('🛑 Multiple chunk load errors in short succession. Aborting reload loop.');
        return new Promise<T>(() => {});
      }
      
      // Set global flag to coordinate with vite:preloadError handler
      (window as any).__VITE_RELOAD_IN_PROGRESS__ = true;
      
      sessionStorage.setItem(RELOAD_KEY, now.toString());
      
      // Give user a moment to see what's happening if we're in a dev environment
      // but in production we want to be as fast as possible
      window.location.reload();
      
      // Return a promise that never resolves to pause router execution while the page reloads
      // This prevents Vue Router from throwing "Couldn't resolve component" errors
      return new Promise<T>(() => {});
    }
    
    // If it's not a chunk load error, just rethrow it
    throw error;
  });
}
